import { ChatSession, WebsocketResponse } from '@klant/conversational-connector/klant-conversational-connector.model';
import { KlantConversationalConnectorService } from '@klant/conversational-connector/klant-conversational-connector.service';
import { AttachChannelModel } from '@klant/conversational-connector/models/AttachChannel.model';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { TypeModel } from '@klant/conversational-connector/models/Type.model';
import { ConversationService } from '@klant/conversational-connector/services/conversation/conversation.service';
import { FaqMessageService } from '@klant/conversational-connector/services/faq-message-service/faq-message.service';
import { InactivityService } from '@klant/conversational-connector/services/inactivity-service/inactivity.service';
import { MessageHandlerService } from '@klant/conversational-connector/services/message-handle-service/message-handler.service';
import { UnreadMessageService } from '@klant/conversational-connector/services/unread-message-service/unread-message.service';
import { WebsocketService } from '@klant/conversational-connector/services/wsservice/websocket.service';

import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';

import { ApContentService } from '@ap/content';
import { ApError } from '@ap/error';

import { SessionStorageService } from '../../../../session-storage/session-storage.service';
import { ChatSessionStore } from '../../../../signal-stores/chat-session-store/chat-session.store';
import { ConversationalChatStore } from '../../../../signal-stores/conversational-chat-store/conversational-chat.store';
import { ConversationalConfigStore } from '../../../../signal-stores/conversational-config-store/conversational-config.store';

@Injectable({ providedIn: 'root' })
export class ConversationLifecycleService {
    private readonly apContentService = inject(ApContentService);
    private readonly chatSessionStore = inject(ChatSessionStore);
    private readonly conversationalChatStore = inject(ConversationalChatStore);
    private readonly conversationalConfigStore = inject(ConversationalConfigStore);
    private readonly conversationService = inject(ConversationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly faqMessageService = inject(FaqMessageService);
    private readonly inactivityService = inject(InactivityService);
    private readonly klantConversationalConnectorService = inject(KlantConversationalConnectorService);
    private readonly messageHandlerService = inject(MessageHandlerService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly unreadMessageService = inject(UnreadMessageService);
    private readonly webSocketService = inject(WebsocketService);

    private readonly platform = this.conversationalChatStore.platform;
    private readonly isInitialLoad = signal(true);

    public constructor() {
        effect(() => {
            this.watchKillSwitches();
        });

        this.listenToSocketErrors();
        this.listenToSocketResponses();
        this.createConversationWhenChatSessionIdIsSet();
    }

    public createConversation(): void {
        const chatSession = this.chatSessionStore.chatSession();
        if (!chatSession) {
            this.setTakeoverError(false);
            return;
        }

        this.initializeSeamly(chatSession);
    }

    private initializeSeamly(chatSession: ChatSession) {
        this.klantConversationalConnectorService.initializeSeamly(chatSession).subscribe({
            next: (conversationResponse: ConversationModel) => {
                this.isInitialLoad.set(false);

                this.conversationalChatStore.setConversation(conversationResponse);
                this.initializeSocket(conversationResponse);
            },
            error: (errorResponse) => {
                if (
                    errorResponse instanceof HttpErrorResponse &&
                    errorResponse.error.error === 'conversation_authentication_api_lookup_failure'
                ) {
                    this.chatSessionStore.resetStore();
                    return;
                }
                this.setTakeoverErrorUnlessRetrying();
            }
        });
    }

    private setTakeoverErrorUnlessRetrying(): void {
        if (this.conversationalChatStore.connectionError() !== 'retry connection error') {
            this.setTakeoverError();
        }
    }

    private listenToSocketErrors(): void {
        this.webSocketService.connectionErrorSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data) => {
                this.conversationalChatStore.setConnectionError(data);
            }
        });
    }

    private listenToSocketResponses(): void {
        this.webSocketService.connectionSubjectResponse.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data) => {
                this.handleSocketResponse(data);
            }
        });
    }

    private createConversationWhenChatSessionIdIsSet(): void {
        effect(() => {
            const chatSession = this.chatSessionStore.chatSession();
            if (!!chatSession) {
                this.createConversation();
            }
        });
    }

    private handleSocketResponse(response: WebsocketResponse | null): void {
        if (!response) {
            return;
        }

        switch (response.type) {
            case TypeModel.ATTACH_CHANNEL:
                this.initializeConversation(response);
                break;
            case TypeModel.START_CONVERSATION:
                this.onStartConversation();
                break;
            case TypeModel.PLAIN_TEXT_MESSAGE:
            case TypeModel.CHOICE_PROMPT_MESSAGE:
            case TypeModel.CTA_MESSAGE:
                this.messageHandlerService.handleIncomingMessage(response);
                break;
            case TypeModel.SERVICE_ATTACH:
                this.inactivityService.handleServiceAttach(response);
                break;
            case TypeModel.PARTICIPANT_MESSAGE:
                this.messageHandlerService.handleParticipantMessage(response);
                break;
            case TypeModel.SERVICE_DATA:
                break;
            default:
                throw new Error(`Invalid websocket response: ${(response satisfies never as WebsocketResponse).type}`);
        }
    }

    private initializeSocket(conversationResponse: ConversationModel): void {
        let conversationSocketUrl: string;

        if (this.sessionStorageService.getSeamlyEnvironmentConfig().seamlyEnvironment === 'DEV') {
            conversationSocketUrl = `ws://${conversationResponse.webSocketLink}`;
        } else {
            conversationSocketUrl = `wss://${this.sessionStorageService.getSeamlyEnvironmentConfig().seamlyApiDomain}${conversationResponse.webSocketLink}`;
        }

        if (this.webSocketService.subject && !this.webSocketService.subject.closed) {
            this.resetConversation();
            return;
        }

        this.webSocketService.connect(conversationSocketUrl, () => {
            this.webSocketService.attachSocketChannel(conversationResponse);
            this.setSeamlyVariables();
        });

        this.webSocketService.subscribeSocket();
    }

    private initializeConversation(attachChannelModel: AttachChannelModel): void {
        if (attachChannelModel.success) {
            this.webSocketService.startConversation();

            if (this.conversationalChatStore.conversation()?.chatMessages.length) {
                !!this.conversationalChatStore.connectionError() ? this.resetConversation() : this.setChatMessages();
            }
        } else {
            this.setTakeoverError();
        }
    }

    private resetConversation(): void {
        this.webSocketService.messageLoading.set(false);
        this.conversationService.getConversation().subscribe({
            next: (conversationResponse: ConversationModel) => {
                this.conversationalChatStore.setConversation(conversationResponse);
                this.conversationalChatStore.setConnectionError(undefined);
                this.setChatMessages();
                this.messageHandlerService.resendPendingMessages();
            },
            error: () => this.setTakeoverError()
        });
    }

    private setChatMessages(): void {
        const conversation = this.conversationalChatStore.conversation();
        let messages = [...(conversation?.chatMessages || [])];

        this.conversationalChatStore.setChatMessages(messages);
        this.conversationalChatStore.setIdleTimerActive(conversation?.resumeConversationPrompt || false);
        this.unreadMessageService.markMessagesAsRead(this.conversationalChatStore.chatMessages());
    }

    private onStartConversation(): void {
        this.conversationalChatStore.setChatFullyLoaded(true);

        this.faqMessageService.sendFaqMessage();
    }

    private setTakeoverError(showReloadOption = true): void {
        this.conversationalChatStore.setTakeoverError(
            new ApError(
                this.apContentService.get('klant.conversational.connector.error.message'),
                '',
                undefined,
                showReloadOption,
                this.apContentService.get('klant.conversational.connector.error.message.reload')
            )
        );
    }

    private setSeamlyVariables(): void {
        const platform = this.platform();
        if (!platform) {
            return;
        }

        this.webSocketService.setSeamlyContextVariables(
            platform,
            this.sessionStorageService.getSeamlyEnvironmentConfig().seamlyEnvironment
        );
    }

    private watchKillSwitches(): void {
        if (this.isInitialLoad()) {
            return;
        }

        const chatFullyLoaded = this.conversationalChatStore.chatFullyLoaded();
        if (!chatFullyLoaded) {
            return;
        }

        const isOperational = this.conversationalConfigStore.isOperational();
        if (!isOperational && this.conversationalConfigStore.doneLoading()) {
            this.webSocketService.disconnect();
            return;
        }
    }
}
